import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/core/lib/utils';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import { formatHours } from '@/shared/utils/formatHours';
import type { MyTaskDto } from '../types';

interface MySummaryTaskCardProps {
  task: MyTaskDto;
}

const PRIORITY_STYLES: Record<string, string> = {
  None: 'bg-muted text-muted-foreground',
  Low: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300',
  Medium: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300',
  High: 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
  Urgent: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400',
};

export function MySummaryTaskCard({ task }: MySummaryTaskCardProps) {
  const { t } = useTranslation('personalSummary');
  const formatInstant = useInstantFormat();

  const priorityStyle = PRIORITY_STYLES[task.priority ?? 'None'] ?? PRIORITY_STYLES.None;
  const priorityLabel = t(`priority.${task.priority ?? 'None'}`);
  const statusLabel = task.statusName ?? t(`statusType.${task.statusType ?? 'Todo'}`);
  const isOverdue = task.overdueDays != null;
  const shortDate = task.dueDate ? formatInstant(task.dueDate) : '—';

  return (
    <Card data-testid={`personal-summary-tasks-card-${task.id}`}>
      <CardContent className="space-y-2 px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-1.5">
            {isOverdue && (
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            )}
            <span className="line-clamp-2 text-sm font-medium">{task.title ?? '—'}</span>
          </div>
          <Badge className={cn('shrink-0', priorityStyle)}>{priorityLabel}</Badge>
        </div>

        <div className="flex items-center gap-1.5">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: task.projectColor ?? '#6b7280' }}
          />
          <span className="min-w-0 truncate text-xs text-muted-foreground">
            {task.projectName ?? '—'}
          </span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">{statusLabel}</span>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {isOverdue ? (
            <span className="font-medium text-destructive">
              {shortDate} (+{task.overdueDays}d)
            </span>
          ) : (
            <span>{shortDate}</span>
          )}
          <span>
            {formatHours(task.trackedHours)}
            {task.estimatedHours != null ? ` / ${formatHours(task.estimatedHours)}` : ''}
            {task.efficiencyPercentage != null && (
              <span className="ml-1.5 font-bold text-primary">
                {task.efficiencyPercentage.toFixed(0)}%
              </span>
            )}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
